/**
 * useOoDay — OO Расстановка (B.12-R8) board backbone. Assembles the day's
 * board from real endpoints (assignments?date= alone omits games with no crew,
 * so we start from bookings):
 *   bookings?date=        — all active games of the day
 *   quests                — crew size / duration / reset per quest
 *   assignments?date=     — current crews (grouped by booking)
 *   assignments/meta      — hall admins + published flag (+ MC assignments)
 *   staff                 — operator roster (name / nickname / fullstack)
 *   rates/operators       — rates + derived skills
 *   operator-availability/all — availability hours per operator for the date
 * Returns a normalised board model + reload. Mutators (assign/AUTO/publish)
 * land in later phases.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";
import { fmtDateApi, addDays, isNightTail } from "../utils/date"; import { firedNotice, game1cNotice } from "@ub/shared/firedKeys"; /* 03.10: «Не записаны (уволены)»; архив 1С: «Игры 1С не меняются» */
import { AVAIL_HOURS, AVAIL_DEFAULT_FREE_AFTER } from "./useAvailGrid"; import { useBoardNames } from "./useBoardNames"; import { boardQuestParams } from "./boardQuest";   // архив 1С (В9): имена уволенных и параметры закрытых квестов на доске
import type { AssignMap, HallAdminMap, OperatorForAssign } from "../screens/oo/scoring";

export interface BoardOperator extends OperatorForAssign {
  nickname: string;
  /** mc_ids this operator is qualified to lead (from operator_mc_rates_v2). */
  mcSkills: string[];
  /** 28.08 (владелец): ставка за каждый МК — 0/нет = за МК не заплатят. */
  mcRates: Record<string, number>;
  /** 02.09 (владелец): роль DC — в списке дежурных DC такие идут первыми. */
  isDc: boolean; /** 16.10: «не показывать в расстановке» (HR) — нет в дропдаунах доски; HR-вкладки и графики видят */ boardHidden?: boolean;
}

export interface BoardGame {
  id: string;
  questId: string;
  shortName: string;
  questName: string;
  branchId: string;
  time: string; // display "HH:MM" (custom_time wins)
  /** ключ сортировки: у ночного хвоста (до 06:00) часы +24 — встаёт в конец смены */
  sortTime: string;
  /** игра — ночной хвост следующего календарного дня (27.08) */
  nightTail: boolean;
  operatorsNeeded: number;
  duration: number;
  resetTime: number;
  crewRule?: string;
  players: number;
  tea: boolean;
  mc: string | null;
  prepaid: boolean;
  prepaidAmount: number;
  source: string | null;
  comment: string | null;
  client: string | null;
  isBirthday: boolean; /** 13.10: «Учеб» — учебная игра, играют сотрудники (строка «Играют» на доске) */ isLearning: boolean; /** 16.10: спецверсия — её длительность для пересечений в «+» */ specialVersionId?: string | null;
}

export interface OoDayResult {
  games: BoardGame[];
  operators: BoardOperator[];
  assignMap: AssignMap;
  /** `${bookingId}_${operatorId}` принятых оператором игр (26.08) — зелёная рамка чипа. */ acceptedSet: Set<string>;
  hallAdmins: HallAdminMap; hallTimes: HallTimesMap; hallState: HallStateMap;
  published: boolean;
  /** 02.09: назначений смены, ещё не разосланных операторам — «!» на «Отправить» */
  unsent: number;
  branches: string[];
  allBranches: string[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  opById: Map<string, BoardOperator>; /** архив 1С (В9, 16.10): подпись человека доски — оператор → имя с /api/staff/names (уволенные) → id */ nameOf: (id: string) => string;
  mcByBooking: McByBooking;
  /** 27.09 (владелец): дежурных DC на день несколько; выбор — черновик, «Ты DC» уходит по «Отправить». */
  dcDuties: DcDuty[];
  addDc: (operatorId: string) => Promise<void>;
  removeDc: (operatorId: string) => Promise<void>;
  /** 09.09 (владелец): часы дежурства DC — по удержанию чипа. */
  setDcTime: (operatorId: string, from: string | null, to: string | null) => Promise<void>;
  /** Persist a draft (bulkAssign diff) + hall admins + MC ops; notify=false — только сохранить (21.08). */
  publish: (
    draft: AssignMap, hallAdmins?: HallAdminMap,
    mcAssignments?: { booking_id: string; mc_id: string; operator_id: string }[], notify?: boolean, hallTimes?: HallTimesMap,
  ) => Promise<{ ok: boolean; error?: string; notify?: NotifyResult; notifyError?: string; fired?: string /* 03.10: «Не записаны (уволены): …» — плашка доски */; game1c?: string /* архив 1С: «Игры 1С не меняются: N пар не сохранены» — та же плашка */ }>;
}
/** Ответ POST /assignments/notify (02.09): кому ушло, кому не доставлено. */
export interface NotifyResult { sent: number; operators: number; notified_names: string[]; failed?: { name: string; reason: string }[] }

interface RawBooking {
  id: string; quest_id: string; time: string; custom_time?: string | null; status?: string;
  type?: string; archived?: number; players?: number; tea?: number | boolean; mc?: string | null;
  prepaid?: number | boolean; prepaid_amount?: number; source?: string | null; comment?: string | null;
  client_name?: string | null; branch_id?: string; quest_short?: string; special_version_title?: string | null; special_version_id?: string | number | null; is_birthday?: number | boolean; is_learning?: number | boolean; quest_duration?: number; quest_operators?: number; quest_reset_time?: number; /* архив 1С (В9): квест брони, и закрытый */
}
interface RawQuest {
  id: string; branch_id: string; name: string; short_name: string; duration?: number;
  reset_time?: number; operators?: number; crew_rule?: string; active?: number;
}
interface RawAssign { booking_id: string; operator_id: string; status?: string }
interface RawStaff {
  id: string; name: string; role?: string; status?: string; custom_perms?: string;
  nickname?: string; fullstack?: number; gender?: string;
}
interface RawRate { operator_id: string; quest_id: string; rate: number }
interface RawMcAssign { booking_id: string | number; mc_id: string; operator_id: string }
/** Дежурный DC: кто, в какие часы и подтвердил ли он, что видел назначение. */
export interface DcDuty { id: string; name: string; from: string | null; to: string | null; accepted: boolean; /** 27.09: «Ты DC» уже разослано */ sent: boolean }

interface RawDc { operator_id: string; name: string; time_from?: string | null; time_to?: string | null; accepted_at?: string | null; sent?: boolean }
/** 28.09: чип админа зала на доске — «Отправить» уже ушло ему (sent) и он принял (accepted). */
export type HallStateMap = Record<string, Record<string, { sent: boolean; accepted: boolean }>>;
interface MetaResp { hall_admins?: Record<string, string[] | string>; hall_admin_times?: HallTimesMap; published?: boolean; unsent?: number; unsent_dc?: number;
  hall_admin_state?: HallStateMap; unsent_ha?: number; loaded_at?: string; mc_assignments?: RawMcAssign[]; dc_duty?: RawDc | null; dc_duties?: RawDc[] }

const toDc = (r: RawDc): DcDuty =>
  ({ id: r.operator_id, name: r.name, from: r.time_from ?? null, to: r.time_to ?? null, accepted: !!r.accepted_at, sent: r.sent !== false });
/** 27.09: дежурных несколько; старый сервер отдавал одного — dc_duty. */
const toDcs = (m: { dc_duties?: RawDc[]; dc_duty?: RawDc | null }): DcDuty[] =>
  (m.dc_duties ?? (m.dc_duty ? [m.dc_duty] : [])).map(toDc);
/** Часы админства «от–до» по филиалу (25.08, владелец): long-press по чипу. */ export type HallTimesMap = Record<string, { from: string | null; to: string | null }>;

/** bookingId → mcId → operatorId[] */
export type McByBooking = Record<string, Record<string, string[]>>;
interface RawMcRate { operator_id: string; mc_id: string; rate?: number }
interface RatesResp { rates?: { results?: RawRate[] }; mcRates?: { results?: RawMcRate[] } }
interface AvailAllResp { results?: Record<string, Record<string, Record<string, string>>> }

function isOperator(s: RawStaff): boolean {
  /* 02.09 (владелец): статуса «стажёр» больше нет — только active (уровень = грейд) */
  return s.status === "active"
    && (s.role === "operator" || (s.custom_perms ?? "").includes("operator") || s.role === "oo" || s.role === "dc");
}

export function useOoDay(date: Date): OoDayResult {
  const [games, setGames] = useState<BoardGame[]>([]);
  const [operators, setOperators] = useState<BoardOperator[]>([]);
  const [assignMap, setAssignMap] = useState<AssignMap>({}); const [acceptedSet, setAcceptedSet] = useState<Set<string>>(() => new Set());
  const [hallAdmins, setHallAdmins] = useState<HallAdminMap>({}); const [hallTimes, setHallTimes] = useState<HallTimesMap>({});
  const [mcByBooking, setMcByBooking] = useState<McByBooking>({});
  const [dcDuties, setDcDuties] = useState<DcDuty[]>([]); const [dcUnsent, setDcUnsent] = useState(0);
  const [hallState, setHallState] = useState<HallStateMap>({}); const [haUnsent, setHaUnsent] = useState(0);   // 28.09
  /** Все филиалы/залы (из branch_id квестов) — для назначения админа зала. */
  const [allBranches, setAllBranches] = useState<string[]>([]);
  const [published, setPublished] = useState(false); const [unsent, setUnsent] = useState(0); const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null); const loadedAt = useRef("");   // 28.09: когда доска загрузилась (для правок админа зала)
  const reload = useCallback(() => setTrigger((n) => n + 1), []);
  const dcPut = useCallback(async (body: Record<string, unknown>) => {
    const r = await api<{ ok: boolean; dc_duties?: RawDc[]; dc_duty?: RawDc | null; unsent_dc?: number }>("/api/assignments/dc-duty", { method: "PUT", body: { date: fmtDateApi(date), ...body } });
    setDcDuties(toDcs(r)); setDcUnsent(Number(r.unsent_dc ?? 0));
  }, [date]);
  const addDc = useCallback((operatorId: string) => dcPut({ operator_id: operatorId, action: "add" }), [dcPut]);
  const removeDc = useCallback((operatorId: string) => dcPut({ operator_id: operatorId, action: "remove" }), [dcPut]);
  const setDcTime = useCallback((operatorId: string, from: string | null, to: string | null) => dcPut({ operator_id: operatorId, action: "times", time_from: from, time_to: to }), [dcPut]);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    const d = fmtDateApi(date);
    const opt = { signal: ac.signal };

    // bookings + quests are required to render the day; the rest are
    // best-effort so one auxiliary endpoint failing (403/500) degrades the
    // board gracefully instead of blanking it.
    /* 27.08 (владелец): смена дня = свои игры с 06:00 + ночной хвост D+1
       (игры до 06:00 — «смена уже стоит на филиале»); публикация ночных
       безопасна: bulk на сервере пишет назначение с датой БРОНИ. */
    const dNext = fmtDateApi(addDays(date, 1)); const startedAt = new Date().toISOString();
    Promise.all([
      api<ApiList<RawBooking>>(`/api/bookings?date=${d}`, opt),
      api<ApiList<RawQuest>>(`/api/quests`, opt),
      api<ApiList<RawAssign>>(`/api/assignments?date=${d}`, opt).catch(() => ({ results: [] as RawAssign[] })),
      api<MetaResp>(`/api/assignments/meta?date=${d}`, opt).catch(() => ({} as MetaResp)),
      api<ApiList<RawStaff>>(`/api/staff`, opt).catch(() => ({ results: [] as RawStaff[] })),
      api<RatesResp>(`/api/rates/operators`, opt).catch(() => ({ rates: { results: [] } } as RatesResp)),
      api<AvailAllResp>(`/api/operator-availability/all`, opt).catch(() => ({ results: {} })),
      api<ApiList<RawBooking>>(`/api/bookings?date=${dNext}`, opt).catch(() => ({ results: [] as RawBooking[] })),
      api<ApiList<RawAssign>>(`/api/assignments?date=${dNext}`, opt).catch(() => ({ results: [] as RawAssign[] })),
      api<MetaResp>(`/api/assignments/meta?date=${dNext}`, opt).catch(() => ({} as MetaResp)),
    ])
      .then(([bk, qs, as, meta, st, rt, av, bkN, asN, metaN]) => {
        if (ac.signal.aborted || abortRef.current !== ac) return; // abortRef — эпоха: ответ уже не выбранного дня игнорируем
        loadedAt.current = meta.loaded_at || startedAt;   // часы сервера: устройство ОО может спешить

        const questMap = new Map<string, RawQuest>();
        (qs.results ?? []).forEach((q) => questMap.set(q.id, q));

        const timeOf = (b: RawBooking) => ((b.custom_time || b.time || "") as string).slice(0, 5);
        const dayRows = (bk.results ?? []).filter((b) => !isNightTail(timeOf(b)));
        const nightRows = (bkN.results ?? []).filter((b) => isNightTail(timeOf(b)));

        const nextGames: BoardGame[] = [...dayRows, ...nightRows]
          .filter((b) => (b.type ?? "booking") === "booking" && b.status !== "cancelled" && !b.archived)
          .map((b) => {
            const q = questMap.get(b.quest_id), qp = boardQuestParams(q, b);
            return {
              id: String(b.id),
              questId: b.quest_id,
              /* 22.09 (владелец): оператора ставят на спецверсию — так её и зовём.
                 У версии нет короткого имени, поэтому title идёт в оба поля. */
              shortName: b.special_version_title || b.quest_short || q?.short_name || q?.name || "—",
              questName: b.special_version_title || q?.name || b.quest_short || "—",
              branchId: b.branch_id ?? q?.branch_id ?? "",
              time: (b.custom_time || b.time || "").slice(0, 5),
              sortTime: isNightTail(timeOf(b)) ? `${String(Number(timeOf(b).slice(0, 2)) + 24)}:${timeOf(b).slice(3)}` : timeOf(b),
              nightTail: isNightTail(timeOf(b)),
              operatorsNeeded: qp.operatorsNeeded, /* архив 1С (В9): закрытого квеста (a1c_*) нет в /api/quests — параметры из брони (boardQuest.ts) */
              duration: qp.duration,
              resetTime: qp.resetTime, specialVersionId: b.special_version_id != null ? String(b.special_version_id) : null,
              crewRule: q?.crew_rule,
              players: Number(b.players ?? 0),
              tea: !!b.tea,
              mc: b.mc && b.mc !== "none" ? b.mc : null,
              prepaid: !!b.prepaid,
              prepaidAmount: Number(b.prepaid_amount ?? 0),
              source: b.source ?? null,
              comment: b.comment ?? null,
              client: b.client_name ?? null,
              isBirthday: !!b.is_birthday, isLearning: !!b.is_learning,
            };
          })
          .sort((a, b) => a.sortTime.localeCompare(b.sortTime));

        const gameIds = new Set(nextGames.map((g) => g.id));
        const nextAssign: AssignMap = {}; const nextAccepted = new Set<string>();
        /* asN — только назначения ночного хвоста (по gameIds), дневные D+1 не наши */
        [...(as.results ?? []), ...(asN.results ?? [])].forEach((a) => {
          if (a.status === "archived" || !gameIds.has(String(a.booking_id))) return;
          (nextAssign[String(a.booking_id)] ??= []).push(String(a.operator_id));
          if (a.status === "accepted" || a.status === "started" || a.status === "done") nextAccepted.add(`${a.booking_id}_${a.operator_id}`);
        });

        const ratesByOp = new Map<string, Record<string, number>>();
        (rt.rates?.results ?? []).forEach((r) => {
          const m = ratesByOp.get(r.operator_id) ?? {};
          m[r.quest_id] = Number(r.rate) || 0;
          ratesByOp.set(r.operator_id, m);
        });

        const mcSkillsByOp = new Map<string, string[]>();
        const mcRatesByOp = new Map<string, Record<string, number>>();
        (rt.mcRates?.results ?? []).forEach((r) => {
          const arr = mcSkillsByOp.get(r.operator_id) ?? [];
          arr.push(String(r.mc_id));
          mcSkillsByOp.set(r.operator_id, arr);
          const m = mcRatesByOp.get(r.operator_id) ?? {};
          m[String(r.mc_id)] = Number(r.rate) || 0;
          mcRatesByOp.set(r.operator_id, m);
        });

        const availAll: Record<string, Record<string, Record<string, string>>> = av.results ?? {};
        const allQuestIds = (qs.results ?? []).map((q) => q.id);

        const nextOps: BoardOperator[] = (st.results ?? [])
          .filter(isOperator)
          .map((s) => {
            const rates = ratesByOp.get(s.id) ?? {};
            const skills = s.fullstack ? allQuestIds : Object.keys(rates);
            const dayAvail = availAll[s.id]?.[d] ?? {};
            /* временный режим (26.08): после дедлайна незаполненный час = свободен */
            const avail = Date.now() > AVAIL_DEFAULT_FREE_AFTER
              ? AVAIL_HOURS.filter((h) => dayAvail[h] !== "busy").map((h) => `${String(h).padStart(2, "0")}:00`)
              : Object.entries(dayAvail).filter(([, status]) => status === "free").map(([h]) => `${String(h).padStart(2, "0")}:00`);
            return {
              id: s.id,
              name: s.name,
              nickname: s.nickname || s.name,
              fullstack: Number(s.fullstack ?? 0),
              skills,
              mcSkills: mcSkillsByOp.get(s.id) ?? [],
              mcRates: mcRatesByOp.get(s.id) ?? {},
              isDc: s.role === "dc" || /"dc"/.test(s.custom_perms ?? ""), boardHidden: /"board_hidden"\s*:\s*true/.test(s.custom_perms ?? ""),
              rates,
              avail,
              gender: (s.gender ?? "").toLowerCase(),
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, "ru"));

        const ha: HallAdminMap = {};
        Object.entries(meta.hall_admins ?? {}).forEach(([branch, ids]) => {   /* 11.10: все админы филиала, не только первый — иначе «Сохранить» стирал второго */
          const list = (Array.isArray(ids) ? ids : [ids]).flatMap((x) => String(x ?? "").split(",")).map((x) => x.trim()).filter(Boolean);
          if (list.length) ha[branch] = list;
        }); setHallTimes(meta.hall_admin_times ?? {});

        const mcb: McByBooking = {};
        [...(meta.mc_assignments ?? []), ...(metaN.mc_assignments ?? [])].forEach((m) => {
          const bid = String(m.booking_id);
          if (!gameIds.has(bid)) return; /* metaN — только МК ночного хвоста */
          (mcb[bid] ??= {});
          (mcb[bid][m.mc_id] ??= []).push(String(m.operator_id));
        });

        setAllBranches(
          Array.from(new Set((qs.results ?? []).map((q) => q.branch_id).filter(Boolean) as string[])).sort(),
        );
        setGames(nextGames);
        setAssignMap(nextAssign); setAcceptedSet(nextAccepted);
        setOperators(nextOps);
        setHallAdmins(ha);
        setMcByBooking(mcb);
        setDcDuties(toDcs(meta)); setDcUnsent(Number(meta.unsent_dc ?? 0));
        setHallState(meta.hall_admin_state ?? {}); setHaUnsent(Number(meta.unsent_ha ?? 0));
        setPublished(!!meta.published);
        setUnsent(Number(meta.unsent ?? 0));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted || abortRef.current !== ac) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить расстановку");
        setLoading(false);
      });

    return () => ac.abort();
  }, [date, trigger]);

  const opById = useMemo(() => new Map(operators.map((o) => [o.id, o])), [operators]); const nameOf = useBoardNames(opById, assignMap, mcByBooking, hallAdmins);
  const branches = useMemo(() => {
    const set = new Set<string>();
    games.forEach((g) => g.branchId && set.add(g.branchId));
    return Array.from(set).sort();
  }, [games]);

  const publish = useCallback(async (
    draft: AssignMap, hallDraft?: HallAdminMap,
    mcAssignments?: { booking_id: string; mc_id: string; operator_id: string }[], notify = true, hallTimesDraft?: HallTimesMap,
  ): Promise<{ ok: boolean; error?: string; notify?: NotifyResult; notifyError?: string; fired?: string; game1c?: string }> => {
    const dateStr = fmtDateApi(date);
    const assignments: { booking_id: string; operator_id: string }[] = [];
    const removals: { booking_id: string; operator_id: string }[] = [];
    for (const [bid, ops] of Object.entries(draft)) {
      for (const op of ops) assignments.push({ booking_id: bid, operator_id: op });
    }
    for (const [bid, ops] of Object.entries(assignMap)) {
      for (const op of ops) {
        if (!(draft[bid] ?? []).includes(op)) removals.push({ booking_id: bid, operator_id: op });
      }
    }
    /* 28.08 (владелец: «выставление на МК работает с ошибками»): снятие
       оператора с МК раньше НЕ сохранялось — сервер удаляет только по явному
       mc_removals, а фронт его не слал. Считаем: было в базе, нет в черновике. */
    const mcRemovals: { booking_id: string; mc_id: string; operator_id: string }[] = [];
    if (mcAssignments) {
      const keep = new Set(mcAssignments.map((m) => `${m.booking_id}_${m.mc_id}_${m.operator_id}`));
      for (const [bid, byMc] of Object.entries(mcByBooking)) {
        for (const [mcId, ops] of Object.entries(byMc)) {
          for (const op of ops) {
            if (!keep.has(`${bid}_${mcId}_${op}`)) mcRemovals.push({ booking_id: bid, mc_id: mcId, operator_id: op });
          }
        }
      }
    }
    try {
      const res = await api<{ ok: boolean; error?: string; conflict?: boolean; skipped?: string[]; stale?: string[]; fired?: string[]; game1c_skipped?: string[] }>("/api/assignments/bulk", {
        method: "POST",
        body: { date: dateStr, assignments, removals, hall_admins: hallDraft ?? hallAdmins, hall_multi: true, hall_admin_times: hallTimesDraft ?? hallTimes, mc_assignments: mcAssignments, mc_removals: mcRemovals, confirm_clear: assignments.length === 0, loaded_at: loadedAt.current },
      });
      if (res && res.ok === false) return { ok: false, error: res.error ?? "Не удалось сохранить" }; const fired = firedNotice(res?.fired, operators) ?? undefined; const game1c = game1cNotice(res?.game1c_skipped) ?? undefined; /* 03.10 (владелец: «продолжи»): уволенных сервер не записал — имена с доски, после reload их строк нет; архив 1С: пары игр 1С сервер не трогает */
      // Снять оператора с отработанной игры нельзя — на ней уже отчёт. Молчать об этом нельзя
      // тоже: иначе «сохранено» означает «часть изменений не применилась», и оператор возвращается.
      /* 28.09: пока доска была открыта, ОО одобрил правку админа зала — её не откатываем */
      if (res?.stale?.length) { reload(); return { ok: false, error: `Пока доска была открыта, правки внесли в другом месте (заявки админа зала, админы залов из дашборда: ${res.stale.length}) — их не откатили, доска обновлена, остальное сохранено.${notify ? " Расстановка НЕ отправлена — проверьте и нажмите «Отправить» ещё раз." : ""}`, fired, game1c }; }
      if (res?.skipped?.length) {
        reload();
        return { ok: false, error: `Не сняты назначения с уже отработанных игр: ${res.skipped.length}. Остальное сохранено.`, fired, game1c };
      }
      /* 02.09 (владелец): результат рассылки больше не глотаем — ОО видит,
         кому ушло и кому НЕ доставлено */
      let notifyRes: NotifyResult | undefined; let notifyError: string | undefined;
      if (notify) {
        try { notifyRes = await api<NotifyResult>("/api/assignments/notify", { method: "POST", body: { date: dateStr } }); }
        catch (e: unknown) { notifyError = e instanceof Error ? e.message : "Рассылка не выполнена"; }
      }
      reload();
      return { ok: true, notify: notifyRes, notifyError, fired, game1c };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка публикации" };
    }
  }, [date, assignMap, hallAdmins, hallTimes, mcByBooking, operators, reload]);

  return { games, operators, assignMap, acceptedSet, hallAdmins, hallTimes, hallState, mcByBooking, dcDuties, addDc, removeDc, setDcTime, allBranches, published, unsent: unsent + dcUnsent + haUnsent, branches, loading, error, reload, opById, nameOf, publish };
}
